import React, { useContext, useEffect, useRef, useState } from "react";
import { InfoBoxTitle, InfoBoxWrapper } from "../Styled";
import { AssigneeBox } from "./Styled";
import { api } from "../../../../shared/hooks/api";
import { error, success } from "../../../../utils/toast";
import { AuthenticationContext } from "../../../../provider/AuthenticationProvider";
import { ROLE } from "../../../../data/enums";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { color } from "../../../../shared/styles";
import UserIcon from "../../../../shared/svg/UserIcon";

const ROLE_DISPLAY: Record<string, string> = {
    factory_manager: "Quản đốc xưởng",
    factory_manager_one: "Quản đốc xưởng Phuy Thùng",
    factory_manager_two: "Quản đốc xưởng Cơ Khí Ô Tô",
    deputy_factory_manager_one: "Phó quản đốc xưởng Phuy Thùng",
    deputy_factory_manager_two: "Phó quản đốc xưởng Cơ Khí Ô Tô",
    first_storage_keeper: "Thủ kho 1",
    second_storage_keeper: "Thủ kho 2",
    storage_keeper: "Thủ kho",
};

const Content = ({
                     onChangeValue,
                     assignee,
                     title,
                     role,
                     locked = false,
                     isUpdating = false,
                 }: {
    onChangeValue: Function;
    assignee: any;
    title: string;
    role?: string;
    locked?: boolean;
    isUpdating?: boolean;
}) => {
    const getUsersQuery = () => {
        const upperRole = role?.toUpperCase();
        if (upperRole === "FACTORY_MANAGER") {
            return `/users/?role=${ROLE.FACTORY_MANAGER_ONE},${ROLE.FACTORY_MANAGER_TWO},${ROLE.DEPUTY_FACTORY_MANAGER_ONE},${ROLE.DEPUTY_FACTORY_MANAGER_TWO}`;
        } else {
            return `/users/?role=${upperRole}`;
        }
    };

    const [{ data, isLoading }] = api.get(getUsersQuery());

    const triggerRef = useRef<HTMLDivElement>(null);
    const [menuWidth, setMenuWidth] = useState<number>(260);
    useEffect(() => {
        const updateWidth = () => {
            const w = triggerRef.current?.offsetWidth || 260;
            setMenuWidth(Math.max(w, 260));
        };
        updateWidth();
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [assignee]);

    const {
        user: { role: currentRole },
    } = useContext(AuthenticationContext);

    const canEdit = (toEditRole?: string) => {
        switch (toEditRole) {
            case "factory_manager":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF].includes(currentRole);
            case "first_storage_keeper":
            case "second_storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF, ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(
                    currentRole,
                );
            case "storage_keeper":
                return [ROLE.MANAGER, ROLE.PRODUCTION_STAFF, ROLE.PURCHASING_STAFF, ROLE.FACTORY_MANAGER_ONE, ROLE.FACTORY_MANAGER_TWO, ROLE.DEPUTY_FACTORY_MANAGER_ONE, ROLE.DEPUTY_FACTORY_MANAGER_TWO].includes(
                    currentRole,
                );
            default:
                return false;
        }
    };

    // Render read-only (locked) view without dropdown when locked
    if (locked) {
        return (
            <AssigneeBox>
                <div className="left">
                    <div className="title">{title}</div>
                    <div className="content locked" style={{ cursor: "default" }}>
                        {isUpdating ? (
                            "Đang cập nhật..."
                        ) : assignee ? (
                            <span className="assignee-item">
                                <span className="assignee-icon"><UserIcon /></span>
                                <span className="assignee-text">
                                    <span
                                        className="assignee-name">{assignee?.display_name || assignee?.username || "Không rõ tên"}</span>
                                    {assignee?.email ? (
                                        <span className="assignee-email">{assignee.email}</span>
                                    ) : null}
                                </span>
                            </span>
                        ) : ("Chưa bàn giao")}
                    </div>
                </div>
            </AssigneeBox>
        );
    }

    return (
        <AssigneeBox>
            <DropdownMenu.Root>
                <div className="left">
                    <div className="title">{title}</div>
                    <DropdownMenu.Trigger asChild>
                        <div ref={triggerRef} className="content"
                             style={{ cursor: canEdit(role) ? "pointer" : "default" }}>
                            {assignee ? (
                                <span className="assignee-item">
                                    <span className="assignee-icon"><UserIcon /></span>
                                    <span className="assignee-text">
                                        <span
                                            className="assignee-name">{assignee?.display_name || assignee?.username || "Không rõ tên"}</span>
                                        {assignee?.email ? (
                                            <span className="assignee-email">{assignee.email}</span>
                                        ) : null}
                                    </span>
                                </span>
                            ) : ("Chưa bàn giao")}
                        </div>
                    </DropdownMenu.Trigger>
                </div>

                <DropdownMenu.Portal>
                    <DropdownMenu.Content
                        side="bottom"
                        align="start"
                        sideOffset={6}
                        style={{
                            background: color.emphasis.NEUTRAL_N0,
                            border: `1px solid ${color.secondary.BORDER}`,
                            borderRadius: 10,
                            padding: 8,
                            zIndex: 10000,
                            boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                            minWidth: menuWidth,
                            width: menuWidth,
                            maxHeight: 320,
                            overflowY: "auto",
                            fontFamily: "Noto Sans",
                        }}
                    >
                        <DropdownMenu.Item disabled style={{
                            padding: "8px 10px",
                            fontSize: 12,
                            color: color.secondary.DARK_GREY,
                            cursor: "default",
                            fontFamily: "Noto Sans",
                        }}>
                            {isLoading ? "Đang tải..." : `Chọn ${ROLE_DISPLAY[role || ""] || "người phụ trách"}`}
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator
                            style={{ height: 1, background: color.secondary.BORDER, margin: "6px 0" }} />
                        <DropdownMenu.Item
                            onSelect={() => onChangeValue(undefined)}
                            style={{
                                padding: "8px 10px",
                                fontSize: 14,
                                cursor: "pointer",
                                borderRadius: 6,
                                fontFamily: "Noto Sans",
                                color: color.secondary.CHARCOAL,
                            }}
                        >
                            Bỏ chọn
                        </DropdownMenu.Item>
                        {(data?.results || []).map((user: any) => (
                            <DropdownMenu.Item
                                key={user.id}
                                onSelect={() => onChangeValue(user)}
                                style={{
                                    padding: "8px 10px",
                                    fontSize: 14,
                                    cursor: "pointer",
                                    borderRadius: 6,
                                    fontFamily: "Noto Sans",
                                    color: color.secondary.CHARCOAL,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                }}
                            >
                                <span>{user.display_name || user.username}</span>
                                {assignee?.id === user.id ? (
                                    <span style={{ color: color.primary.BLUE_B100 }}>✓</span>
                                ) : null}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
        </AssigneeBox>
    );
};

interface AssigneeField {
    title: string;
    fieldName: string;
    role?: string;
}

export const Assignees = ({
                              data: materialRequest,
                              updateData,
                              fetchData,
                              mergeMaterialRequest,
                              fields,
                              disabledEdit = false,
                          }: {
    data: any;
    updateData: Function;
    fetchData?: any;
    mergeMaterialRequest?: Function;
    fields: AssigneeField[];
    disabledEdit?: boolean;
}) => {
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    const onChangeAssignee = (field: string, role?: string, currentAssignee?: any) => async (assignee: any) => {
        if (updatingField) return;
        setUpdatingField(field);
        try {
            let updatePayload: Record<string, any>;
            console.log("field:", field, "role:", role, "assignee:", assignee, "currentAssignee:", currentAssignee);
            if (assignee?.id) {
                const fieldWithId = `${field}_id`;
                updatePayload = { [fieldWithId]: assignee.id };
            } else {
                const assigneeRole = currentAssignee?.role?.toLowerCase();
                if (assigneeRole === "factory_manager_one" || assigneeRole === "factory_manager_two") {
                    updatePayload = { factory_manager_id: "" };
                } else if (assigneeRole === "deputy_factory_manager_one" || assigneeRole === "deputy_factory_manager_two") {
                    updatePayload = { deputy_factory_manager_id: "" };
                } else {
                    updatePayload = { [`${field}_id`]: "" };
                }
            }

            await updateData(updatePayload);

            // Optimistically update local state to avoid full refetch and flicker
            if (mergeMaterialRequest) {
                const localUpdatePayload = assignee
                    ? { [field]: assignee }
                    : { [field]: null };
                mergeMaterialRequest(localUpdatePayload);
            } else if (fetchData) {
                // Fallback to refetch if mergeMaterialRequest is not available
                await fetchData();
            }

            const roleText = ROLE_DISPLAY[role || ""] || "Người phụ trách";
            const actionText = assignee?.id ? "Bàn giao" : "Bỏ chọn";
            const nameText = assignee?.display_name || assignee?.username || "";

            success(
                "THÀNH CÔNG",
                `${actionText}${nameText ? ` - ${nameText}` : ""}${roleText ? ` (${roleText})` : ""} thành công`,
            );
        } catch (err) {
            const roleText = ROLE_DISPLAY[role || ""] || "Người phụ trách";
            error(`${roleText} - cập nhật thất bại`);
        } finally {
            setUpdatingField(null);
        }
    };

    const getAssigneeValue = (fieldName: string) => {
        // For factory_manager field, check both factory_manager and deputy_factory_manager
        if (fieldName === "factory_manager") {
            return (materialRequest as any).factory_manager || (materialRequest as any).deputy_factory_manager;
        }
        return (materialRequest as any)[fieldName as any];
    };

    return (
        <InfoBoxWrapper>
            <InfoBoxTitle>Phụ trách</InfoBoxTitle>
            {fields.map((item) => {
                const currentAssignee = getAssigneeValue(item.fieldName);
                return (
                    <Content
                        key={item.fieldName}
                        title={item.title}
                        onChangeValue={onChangeAssignee(item.fieldName, item.role, currentAssignee)}
                        assignee={currentAssignee}
                        role={item.role}
                        locked={disabledEdit || updatingField !== null}
                        isUpdating={updatingField === item.fieldName}
                    />
                );
            })}
        </InfoBoxWrapper>
    );
};
